import { useEffect, useRef, useState, useCallback } from "react";
import { Send, Timer } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useChat } from "@/context/ChatContext";
import { ChatMessage } from "@/components/chat/ChatMessage";
import { toast } from "sonner";

function useCountdown(target) {
  const [left, setLeft] = useState("");
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const ms = new Date(target).getTime() - Date.now();
      if (ms <= 0) { setLeft("sıfırlanıyor..."); return; }
      const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000), s = Math.floor((ms % 60000) / 1000);
      setLeft(`${h > 0 ? h + "s " : ""}${m}dk ${s}sn`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [target]);
  return left;
}

export function PublicChat() {
  const { open } = useChat();
  const [msgs, setMsgs] = useState([]);
  const [nextReset, setNextReset] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const lastRef = useRef(null);
  const loadedRef = useRef(false);
  const bottomRef = useRef(null);
  const countdown = useCountdown(nextReset);

  const poll = useCallback(async () => {
    try {
      const params = lastRef.current ? { after: lastRef.current } : {};
      const { data } = await api.get("/chat/public", { params });
      setNextReset(data.next_reset);
      if (data.messages.length) {
        lastRef.current = data.messages[data.messages.length - 1].created_at;
        setMsgs((prev) => {
          const ids = new Set(prev.map((m) => m.id));
          return [...prev, ...data.messages.filter((m) => !ids.has(m.id))];
        });
        if (loadedRef.current) {
          data.messages.filter((m) => m.role === "system" && m.kind === "join").forEach((m) => toast(m.text, { icon: "👋" }));
          if (data.messages.some((m) => m.kind === "reset")) { setMsgs(data.messages); }
        }
      }
      loadedRef.current = true;
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 3000);
    return () => clearInterval(t);
  }, [poll]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length, open]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/chat/public", { text });
      setMsgs((p) => [...p, data]);
      lastRef.current = data.created_at;
      setText("");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col" data-testid="public-chat">
      <div className="px-5 py-2 border-b border-slate-800/70 text-[11px] text-slate-500 flex items-center gap-2 font-mono-cx" data-testid="chat-reset-countdown">
        <Timer className="h-3.5 w-3.5 text-cyan-400" /> Sohbet 2 saatte bir sıfırlanır · Kalan: <span className="text-cyan-300">{countdown || "—"}</span>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-3" data-testid="public-chat-messages">
        {msgs.length === 0 && <p className="text-center text-xs text-slate-600 py-10">Henüz mesaj yok. İlk mesajı sen yaz!</p>}
        {msgs.map((m) => <ChatMessage key={m.id} msg={m} />)}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="p-4 border-t border-slate-800 flex gap-2">
        <input data-testid="public-chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Mesaj yaz..." maxLength={1000} className="cx-input flex-1 px-4 text-sm outline-none" />
        <button data-testid="public-chat-send" type="submit" disabled={sending || !text.trim()} className="h-11 w-11 rounded-lg cx-btn-primary flex items-center justify-center disabled:opacity-50"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
}
